// Utility for tracking recently visited podcast episodes in localStorage
// We only persist at most the N most recent (default 3)

export type RecentEpisode = {
  id: string
  title: string
  visitedAt: number // epoch ms
}

const STORAGE_KEY = 'recent_podcast_episodes'
const LIMIT = 3

function isRecentEpisode(v: unknown): v is RecentEpisode {
  return v !== null && 
         typeof v === 'object' && 
         'id' in v && 
         'title' in v && 
         'visitedAt' in v &&
         typeof (v as RecentEpisode).id === 'string' &&
         typeof (v as RecentEpisode).title === 'string' &&
         typeof (v as RecentEpisode).visitedAt === 'number'
}

function readStore(): RecentEpisode[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isRecentEpisode).slice(0, LIMIT)
  } catch {
    return []
  }
}

function writeStore(list: RecentEpisode[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, LIMIT)))
    // Dispatch a custom event so in-tab listeners can update immediately
    window.dispatchEvent(new Event('recent-episodes:changed'))
  } catch {
    // ignore
  }
}

export function getRecentEpisodes(): RecentEpisode[] {
  return readStore()
}

export function addRecentEpisode(ep: { id: string; title?: string | null }) {
  if (!ep.id) return
  const list = readStore()
  const existingIndex = list.findIndex(e => e.id === ep.id)
  if (existingIndex !== -1) {
    // Do not reorder; optionally update title if changed and non-empty
    const existing = list[existingIndex]
    const newTitle = ep.title || 'Untitled podcast'
    if (existing.title !== newTitle) {
      list[existingIndex] = { ...existing, title: newTitle }
      writeStore(list)
    }
    return
  }
  // Not present -> add to front (most recently ADDED, not updated)
  const newEntry: RecentEpisode = { id: ep.id, title: ep.title || 'Untitled podcast', visitedAt: Date.now() }
  list.unshift(newEntry)
  writeStore(list.slice(0, LIMIT))
}

export function removeRecentEpisode(id: string) {
  const next = readStore().filter(e => e.id !== id)
  writeStore(next)
}

// React hook to subscribe to changes (storage events + custom event for same-tab updates)
import React from 'react'
export function useRecentEpisodes() {
  const [episodes, setEpisodes] = React.useState<RecentEpisode[]>(() => getRecentEpisodes())
  React.useEffect(() => {
    const handler = () => setEpisodes(getRecentEpisodes())
    window.addEventListener('storage', handler)
    window.addEventListener('recent-episodes:changed', handler)
    return () => {
      window.removeEventListener('storage', handler)
      window.removeEventListener('recent-episodes:changed', handler)
    }
  }, [])
  return episodes
}
